import React, { useContext, useEffect, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { EventBasedTriggerMetadata } from '@reach/shared-types/business-automation';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import { useScrollIndicator } from '../useScrollIndicator';
import { useSegmentDefinitions } from '../useSegmentDefinitions';
import { SegmentListStates } from './SegmentListStates';
import styles from './TriggerStep.module.css';

interface SegmentTriggerConfigDialogProps {
  onClose: () => void;
  triggerMetadata: EventBasedTriggerMetadata;
}

export const SegmentTriggerConfigDialog: React.FC<SegmentTriggerConfigDialogProps> = ({
  onClose,
  triggerMetadata,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateTrigger } = editor;
  const { data: segmentDefinitions, isLoading, error } = useSegmentDefinitions();

  const currentSegmentId = triggerMetadata.eventFilter?.segment_id ?? null;
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(currentSegmentId);
  const [customTriggerCopy, setCustomTriggerCopy] = useState(triggerMetadata.customTriggerCopy ?? '');
  const { mainRef, hasMoreBelow, onScroll, checkScroll } = useScrollIndicator();

  useEffect(() => {
    setSelectedSegmentId(triggerMetadata.eventFilter?.segment_id ?? null);
    setCustomTriggerCopy(triggerMetadata.customTriggerCopy ?? '');
  }, [triggerMetadata]);

  useEffect(() => {
    checkScroll();
  }, [segmentDefinitions, checkScroll]);

  const handleOK = () => {
    const newTriggerMetadata = structuredClone(triggerMetadata);
    newTriggerMetadata.eventFilter.segment_id = selectedSegmentId || '';
    newTriggerMetadata.customTriggerCopy = customTriggerCopy || undefined;
    updateTrigger(newTriggerMetadata);
    onClose();
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.segmentTriggerDialog} open>
        <header>
          <hgroup>
            <h2>Select Audience Segment</h2>
            <p>Choose which segment will trigger this campaign</p>
          </hgroup>
          <button onClick={onClose} type="button" aria-label="Close">
            <X />
          </button>
        </header>

        <section className={styles.triggerCopySection}>
          <label>
            Trigger label
            <input
              type="text"
              value={customTriggerCopy}
              onChange={e => setCustomTriggerCopy(e.target.value)}
              placeholder="e.g. When a new lead signs up"
            />
          </label>
          <p>Optional — shown to customers in the automation view.</p>
        </section>

        <div className={styles.mainWrapper}>
          <main ref={mainRef} onScroll={onScroll}>
            <SegmentListStates
              isLoading={isLoading}
              error={error}
              isEmpty={!segmentDefinitions?.length}
            />
            {segmentDefinitions && segmentDefinitions.length > 0 && (
              <ul>
                {segmentDefinitions.map(segment => {
                  const { id, name, description, canonicalVersionId } = segment;
                  const isSelected = selectedSegmentId === canonicalVersionId;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        data-selected={isSelected || undefined}
                        onClick={() => setSelectedSegmentId(canonicalVersionId)}>
                        <strong>{name}</strong>
                        {description && <p>{description}</p>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </main>
          {hasMoreBelow && (
            <div className={styles.scrollIndicator}>
              <span className={styles.scrollIndicatorPill}>
                <ChevronDown size={12} />
                More below
              </span>
            </div>
          )}
        </div>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>
    </>
  );
};
